import React, { useState } from "react";
import { Button, Modal, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaDownload, FaEye, FaUpload } from "react-icons/fa";
import api from "../../../api";
import swal from "sweetalert";
import FilePreview from "./FilePreview";

const getFilenameFromDisposition = (disposition, fallback) => {
 if (!disposition) return fallback;

 const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
 if (encodedMatch?.[1]) {
 try {
 return decodeURIComponent(encodedMatch[1]);
 } catch (_) {
 return encodedMatch[1];
 }
 }

 const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
 return plainMatch?.[1] || fallback;
};

const SignedBillActions = ({
 id,
 type,
 signedCopy,
 onUploadSuccess,
 canUpload = true,
 canDownload = true,
}) => {
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [file, setFile] = useState(null);
 const [uploading, setUploading] = useState(false);
 const [previewing, setPreviewing] = useState(false);
 const [downloading, setDownloading] = useState(false);

 const hasSignedCopy = Boolean(signedCopy?.filePath || signedCopy?.fileUrl || signedCopy?.fileName);

 const handleFileChange = (e) => {
 const selectedFile = e.target.files?.[0];
 if (!selectedFile) {
 setFile(null);
 return;
 }

 const isPdf =
 selectedFile.type === "application/pdf" ||
 selectedFile.name.toLowerCase().endsWith(".pdf");

 if (!isPdf) {
 setFile(null);
 e.target.value = "";
 return swal("Error", "Please select a PDF file only", "error");
 }

 setFile(selectedFile);
 };

 const handleUpload = async () => {
 if (!file) {
 return swal("Error", "Please select a PDF file to upload", "error");
 }

 setUploading(true);
 const formData = new FormData();
 formData.append("signedCopy", file);

 try {
 const res = await api.patch(`/${type}/${id}/upload-signed-copy`, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", "Signed bill uploaded successfully", "success");
 setShowUploadModal(false);
 setFile(null);
 if (onUploadSuccess) await onUploadSuccess();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to upload signed bill", "error");
 } finally {
 setUploading(false);
 }
 };

 const handleDownload = async () => {
 if (!hasSignedCopy) return;

 setDownloading(true);
 try {
 const res = await api.get(`/${type}/${id}/download-signed-copy`, {
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });

 const blobUrl = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );
 const link = document.createElement("a");
 link.href = blobUrl;
 link.download = getFilenameFromDisposition(
 res.headers?.["content-disposition"],
 `${type}-signed-copy.pdf`
 );
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(blobUrl);
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to download signed bill", "error");
 } finally {
 setDownloading(false);
 }
 };

 const handlePreview = async () => {
 if (!hasSignedCopy) return;

 setPreviewing(true);
 const previewWindow = window.open("", "_blank");
 try {
 const res = await api.get(`/${type}/${id}/download-signed-copy`, {
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });

 const blobUrl = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" })
 );

 if (previewWindow) {
 previewWindow.location.href = blobUrl;
 } else {
 window.open(blobUrl, "_blank");
 }
 } catch (error) {
 if (previewWindow) previewWindow.close();
 swal("Error", error.response?.data?.message || "Failed to preview signed bill", "error");
 } finally {
 setPreviewing(false);
 }
 };

 const tooltip = (text) => <Tooltip id={`signed-bill-${id}`}>{text}</Tooltip>;

 return (
 <>
 <div className="d-flex align-items-center gap-1">
 {canDownload && (
 <OverlayTrigger placement="top" overlay={tooltip(hasSignedCopy ? "Preview signed bill" : "Signed bill not available")}>
 <span>
 <Button
 variant="outline-info"
 size="sm"
 className="p-1 px-2 shadow-none"
 disabled={!hasSignedCopy || previewing}
 onClick={handlePreview}
 >
 {previewing ? <Spinner animation="border" size="sm" /> : <FaEye />}
 </Button>
 </span>
 </OverlayTrigger>
 )}

 {canDownload && (
 <OverlayTrigger placement="top" overlay={tooltip(hasSignedCopy ? "Download signed bill" : "Signed bill not available")}>
 <span>
 <Button
 variant="outline-success"
 size="sm"
 className="p-1 px-2 shadow-none"
 disabled={!hasSignedCopy || downloading}
 onClick={handleDownload}
 >
 {downloading ? <Spinner animation="border" size="sm" /> : <FaDownload />}
 </Button>
 </span>
 </OverlayTrigger>
 )}

 {canUpload && (
 <OverlayTrigger placement="top" overlay={tooltip(hasSignedCopy ? "Replace signed bill" : "Upload signed bill")}>
 <Button
 variant="outline-primary"
 size="sm"
 className="p-1 px-2 shadow-none"
 onClick={() => setShowUploadModal(true)}
 >
 <FaUpload />
 </Button>
 </OverlayTrigger>
 )}
 </div>

 <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
 <Modal.Header closeButton>
 <Modal.Title className="fs-5">Signed Bill</Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-3">
 <div className="small text-muted mb-2">
 Upload the signed PDF for this bill. The file can be downloaded later from the same row.
 </div>

 {signedCopy && (signedCopy.filePath || signedCopy.fileUrl || signedCopy.fileName) && (
 <div className="mb-3">
 <label className="form-label small fw-bold">Existing Signed Copy:</label>
 <FilePreview file={signedCopy} />
 </div>
 )}

 <Form.Group>
 <Form.Label className="small fw-bold">
 {hasSignedCopy ? "Replace with New PDF:" : "Select PDF File:"}
 </Form.Label>
 <Form.Control type="file" accept="application/pdf" onChange={handleFileChange} size="sm" />
 </Form.Group>

 {file && (
 <div className="mt-3">
 <label className="form-label small fw-bold text-primary">New Selection:</label>
 <FilePreview file={file} onRemove={() => setFile(null)} />
 </div>
 )}
 </Modal.Body>
 <Modal.Footer className="p-2">
 <Button variant="light" size="sm" onClick={() => setShowUploadModal(false)}>
 Cancel
 </Button>
 <Button variant="primary" size="sm" onClick={handleUpload} disabled={uploading || !file}>
 {uploading ? (
 <>
 <Spinner animation="border" size="sm" className="me-1" />
 Uploading
 </>
 ) : hasSignedCopy ? (
 "Replace"
 ) : (
 "Upload"
 )}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
};

export default SignedBillActions;
